import {
  DndContext,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import type { DragEndEvent, DragOverEvent, DragStartEvent } from '@dnd-kit/core'
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { DragIndicator, Visibility } from '@mui/icons-material'
import { Box, Button, Dialog, IconButton, Typography } from '@mui/material'
import { type Theme, alpha, darken, lighten } from '@mui/material/styles'
import type { ChapterSectionDependency } from '@shared/chapterSectionDependencies'
import { chapterSectionKey } from '@shared/chapterSectionsOrder'
import { ELEMENTS_ROOT_TAG_ID } from '@shared/tags/canonicalElementsTags'
import type { TagDefinition } from '@shared/tags/tagRegistry'
import { type MouseEvent, type ReactNode, useCallback, useMemo, useState } from 'react'
import toast from 'react-hot-toast'

import SearchPreviewPanel from '../../../../components/search/SearchPreviewPanel'
import { type Book, type ContentFile, apiService } from '../../../../services/api'
import { logger } from '../../../../utils/logger'
import { sectionTagsAnnotationId } from '../../../../utils/sectionTagsAnnotationId'
import type { ChapterSectionEntry } from '../../hooks/useChapterSectionsBook'
import type { SectionDependencyView } from '../../hooks/useSectionDependencies'
import { useSectionPreview } from '../../hooks/useSectionPreview'
import SectionDependencyChip from '../dependencies/SectionDependencyChip'

type TagMap = Record<string, TagDefinition>

const LABEL_W = 260
const COL_W = 96
const ROW_H = 34
const HEADER_H = 44 // fixed so the zone dividers can stick just below the column header
const PLACED_ID = '__mx_placed__'
const UNPLACED_ID = '__mx_unplaced__'

// Sticky headers/labels get a small lift off the cell surface (mirrors the elements×chapters matrix).
const labelBg = (theme: Theme) =>
  theme.palette.mode === 'dark'
    ? lighten(theme.palette.background.default, 0.06)
    : darken(theme.palette.background.default, 0.04)

// Order-sensitive: the first element is the section's primary, so a reorder is a real change.
const sameList = (a: string[], b: string[]): boolean => a.length === b.length && a.every((x, i) => x === b[i])

function elementFamilyIds(tags: TagMap): Set<string> {
  const ids = new Set<string>()
  for (const [id, def] of Object.entries(tags)) {
    if ((def.parentIds || []).includes(ELEMENTS_ROOT_TAG_ID)) {
      ids.add(id)
    }
  }
  return ids
}

/**
 * A single (section, element) toggle. Marked cells share one shape (a filled square); the section's
 * **primary** (home) thread shows at full strength, **secondary** threads at reduced intensity — the
 * distinction is colour weight, not shape. Plain click toggles membership; modifier-click
 * (⌘/Ctrl/Alt) promotes the element to primary.
 */
function ToggleCell({
  marked,
  primary,
  color,
  title,
  onClick,
}: {
  marked: boolean
  primary: boolean
  color?: string
  title: string
  onClick: (e: MouseEvent) => void
}) {
  const mark = color || 'primary.main'
  return (
    <Box
      onClick={onClick}
      title={title}
      sx={(theme) => {
        const base = color || theme.palette.primary.main
        const fill = primary ? 0.16 : 0.07
        return {
          height: ROW_H,
          borderInlineEnd: '1px solid',
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          bgcolor: marked ? alpha(base, fill) : 'transparent',
          '&:hover': { bgcolor: marked ? alpha(base, fill + 0.12) : theme.palette.action.hover },
        }
      }}
    >
      {marked && <Box sx={{ width: 14, height: 14, borderRadius: '4px', bgcolor: mark, opacity: primary ? 1 : 0.4 }} />}
    </Box>
  )
}

// A body row: a subgrid spanning all columns so it's one hover target while its cells still align to
// the parent grid's element columns. The whole row highlights subtly on hover.
const rowSx = (theme: Theme) => ({
  display: 'grid',
  gridTemplateColumns: 'subgrid' as const,
  gridColumn: '1 / -1',
  '&:hover': { backgroundColor: alpha(theme.palette.text.primary, 0.022) },
  '&:hover .mx-rowlabel': { backgroundColor: alpha(theme.palette.text.primary, 0.04) },
})

/**
 * A drop zone (Placed / Unplaced) that is itself a subgrid spanning all columns, so its rows keep
 * aligning to the parent grid's element columns. `minHeight` keeps an empty zone droppable.
 */
function ZoneDroppable({ id, children }: { id: string; children: ReactNode }) {
  const { setNodeRef, isOver } = useDroppable({ id })
  return (
    <Box
      ref={setNodeRef}
      sx={(theme) => ({
        display: 'grid',
        gridTemplateColumns: 'subgrid',
        gridColumn: '1 / -1',
        minHeight: ROW_H,
        transition: 'background-color 0.15s ease',
        backgroundColor: isOver ? alpha(theme.palette.primary.main, 0.05) : 'transparent',
      })}
    >
      {children}
    </Box>
  )
}

/** A row wrapped as a dnd-kit sortable; the drag handle is passed down to a grip in the label. */
function SortableMatrixRow({ id, children }: { id: string; children: (handle: Record<string, unknown>) => ReactNode }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id })
  return (
    <Box
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.4 : 1,
        zIndex: isDragging ? 5 : undefined,
      }}
      sx={rowSx}
    >
      {children({ ...attributes, ...listeners })}
    </Box>
  )
}

interface SectionElementMatrixDialogProps {
  open: boolean
  onClose: () => void
  entries: ChapterSectionEntry[]
  entryByKey: Map<string, ChapterSectionEntry>
  bookId?: string
  bookDirection?: 'ltr' | 'rtl'
  tagRegistry?: TagMap
  tagColorMap?: Record<string, string>
  onSectionTagsPersisted?: () => void
  /** Persists the flat reading order (book.settings.flatSectionsOrder). Enables drag-to-reorder of Placed rows. */
  onFlatSectionsOrderChange?: (next: string[]) => Promise<void>
  book?: Book | null
  onSectionContentSaved?: (filename: string, updatedFile: ContentFile) => void
  /** Shared dependency view; the chip renders only for sections that have deps. */
  dependencies?: SectionDependencyView
  onEditDependency?: (dep: ChapterSectionDependency) => void
  onRemoveDependency?: (depId: string) => void
}

/**
 * Sections × elements presence matrix. Rows are ChapterSections in the by-section (flat) order —
 * Placed (flatSectionsOrder) then Unplaced — and each row's marked cells are the יסודות represented
 * in that section. A section may carry several elements (checkbox semantics); marks persist as
 * section tags (the element-family subset), leaving the section's other tags untouched.
 */
export default function SectionElementMatrixDialog({
  open,
  onClose,
  entries,
  entryByKey,
  bookId,
  bookDirection = 'ltr',
  tagRegistry,
  tagColorMap,
  onSectionTagsPersisted,
  onFlatSectionsOrderChange,
  book,
  onSectionContentSaved,
  dependencies,
  onEditDependency,
  onRemoveDependency,
}: SectionElementMatrixDialogProps) {
  const familyIds = useMemo(() => elementFamilyIds(tagRegistry ?? {}), [tagRegistry])

  const elementList = useMemo(() => {
    const tags = tagRegistry ?? {}
    return Object.entries(tags)
      .filter(([, def]) => (def.parentIds || []).includes(ELEMENTS_ROOT_TAG_ID))
      .map(([id, def]) => ({ id, name: def.name, order: typeof def.order === 'number' ? def.order : 0 }))
      .sort((a, b) => a.order - b.order || a.name.localeCompare(b.name, 'he'))
      .map(({ id, name }) => ({ id, name }))
  }, [tagRegistry])

  // Placed = flatSectionsOrder (kept, in order). Unplaced is derived from the live draft below.
  const placedKeys = useMemo(() => {
    const flatOrder = book?.settings?.flatSectionsOrder ?? []
    const known = new Set(entries.map((e) => chapterSectionKey(e.chapter.filename, e.headingBlockId)))
    return flatOrder.filter((k) => known.has(k))
  }, [book?.settings?.flatSectionsOrder, entries])

  const allKeys = useMemo(() => entries.map((e) => chapterSectionKey(e.chapter.filename, e.headingBlockId)), [entries])

  // Draft: sectionKey -> ordered element ids (index 0 = the section's primary/home thread). Seeded
  // once from the section tags, preserving order (persisted home-first).
  const [selectedByKey, setSelectedByKey] = useState<Record<string, string[]>>(() => {
    const map: Record<string, string[]> = {}
    for (const e of entries) {
      const key = chapterSectionKey(e.chapter.filename, e.headingBlockId)
      map[key] = e.tagIds.filter((t) => familyIds.has(t))
    }
    return map
  })
  const [dirty, setDirty] = useState(false)
  const [saving, setSaving] = useState(false)

  // --- flat reorder: drag rows within/between Placed and Unplaced ---
  // Fully STAGED, like the element marks: reorders live only in local drafts (seeded once at mount;
  // the dialog remounts per open) and are committed to book.settings.flatSectionsOrder only on Save —
  // so Cancel/close discards them and the sidebar TOC updates only once saved. Same single storage as
  // the TOC; only the Placed order is stored (Unplaced order is ephemeral).
  const flatDragEnabled = Boolean(onFlatSectionsOrderChange)
  const unplacedFromProps = useMemo(() => {
    const ps = new Set(placedKeys)
    return allKeys.filter((k) => !ps.has(k))
  }, [allKeys, placedKeys])
  const [placedDraft, setPlacedDraft] = useState<string[]>(placedKeys)
  const [unplacedDraft, setUnplacedDraft] = useState<string[]>(unplacedFromProps)
  const [activeDragId, setActiveDragId] = useState<string | null>(null)

  const dragSensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 300, tolerance: 5 } }),
  )

  // Which zone an id belongs to (a zone id itself, or the row's current draft membership).
  const containerOf = useCallback(
    (id: string): typeof PLACED_ID | typeof UNPLACED_ID | null => {
      if (id === PLACED_ID || placedDraft.includes(id)) {
        return PLACED_ID
      }
      if (id === UNPLACED_ID || unplacedDraft.includes(id)) {
        return UNPLACED_ID
      }
      return null
    },
    [placedDraft, unplacedDraft],
  )

  const onDragStart = useCallback((e: DragStartEvent) => setActiveDragId(String(e.active.id)), [])

  // Cross-zone move mid-drag: pull the active id out of its zone and splice it into the target zone
  // at the hovered row's position (or the end when hovering the empty zone area). Persisted on drop.
  const onDragOver = useCallback(
    (e: DragOverEvent) => {
      const { active, over } = e
      if (!over) {
        return
      }
      const activeId = String(active.id)
      const overId = String(over.id)
      const from = containerOf(activeId)
      const to = containerOf(overId)
      if (!from || !to || from === to) {
        return
      }
      const insertInto = (list: string[]): string[] => {
        if (list.includes(activeId)) {
          return list
        }
        const idx = overId === to ? list.length : list.indexOf(overId)
        const next = [...list]
        next.splice(idx === -1 ? list.length : idx, 0, activeId)
        return next
      }
      if (to === PLACED_ID) {
        setUnplacedDraft((prev) => prev.filter((k) => k !== activeId))
        setPlacedDraft(insertInto)
      } else {
        setPlacedDraft((prev) => prev.filter((k) => k !== activeId))
        setUnplacedDraft(insertInto)
      }
    },
    [containerOf],
  )

  // On drop, finalise the same-zone reorder in the draft (cross-zone was already applied in dragOver).
  const onDragEnd = useCallback(
    (e: DragEndEvent) => {
      setActiveDragId(null)
      const { active, over } = e
      if (!over || active.id === over.id) {
        return
      }
      const activeId = String(active.id)
      const overId = String(over.id)
      const zone = containerOf(activeId)
      if (!zone || zone !== containerOf(overId)) {
        return
      }
      const reorder = (prev: string[]): string[] => {
        const oi = prev.indexOf(activeId)
        const ni = prev.indexOf(overId)
        return oi === -1 || ni === -1 ? prev : arrayMove(prev, oi, ni)
      }
      if (zone === PLACED_ID) {
        setPlacedDraft(reorder)
      } else {
        setUnplacedDraft(reorder)
      }
    },
    [containerOf],
  )

  const placedRows = flatDragEnabled ? placedDraft : placedKeys
  const unplacedRows = flatDragEnabled ? unplacedDraft : unplacedFromProps
  const activeDragEntry = activeDragId ? entryByKey.get(activeDragId) : null
  const flatDirty = flatDragEnabled && !sameList(placedDraft, placedKeys)
  const isDirty = dirty || flatDirty

  const {
    previewKey,
    previewAst,
    previewLoading,
    previewError,
    previewTitle,
    previewDirty,
    previewSaving,
    previewRecentlySaved,
    openPreview,
    closePreview,
    onPreviewChange,
    onPreviewSave,
  } = useSectionPreview({ open, bookId, entryByKey, onSectionContentSaved })

  // Plain click toggles membership (new marks append as secondary); the primary (index 0) is never
  // implicitly reordered here.
  const toggleCell = useCallback((key: string, elementId: string) => {
    setSelectedByKey((prev) => {
      const cur = prev[key] ?? []
      const next = cur.includes(elementId) ? cur.filter((x) => x !== elementId) : [...cur, elementId]
      return { ...prev, [key]: next }
    })
    setDirty(true)
  }, [])

  // Modifier-click promotes an element to primary (moves it to the front, adding it if absent).
  const setPrimary = useCallback((key: string, elementId: string) => {
    setSelectedByKey((prev) => {
      const cur = prev[key] ?? []
      if (cur[0] === elementId) {
        return prev
      }
      return { ...prev, [key]: [elementId, ...cur.filter((x) => x !== elementId)] }
    })
    setDirty(true)
  }, [])

  const elementColCount = useCallback(
    (elementId: string): number => allKeys.filter((k) => selectedByKey[k]?.includes(elementId)).length,
    [allKeys, selectedByKey],
  )

  const handleClose = useCallback(() => {
    if (isDirty && !window.confirm('לצאת בלי לשמור? השינויים שעשית יימחקו.')) {
      return
    }
    onClose()
  }, [isDirty, onClose])

  const handleSave = useCallback(async () => {
    if (!bookId || saving) {
      return
    }
    setSaving(true)
    try {
      let changed = false
      for (const entry of entries) {
        const key = chapterSectionKey(entry.chapter.filename, entry.headingBlockId)
        const selected = selectedByKey[key] ?? []
        const persisted = entry.tagIds.filter((t) => familyIds.has(t))
        if (sameList(selected, persisted)) {
          continue
        }
        // Element ids first (primary → secondaries, in order), then the section's other tags.
        const nextTags = [...selected, ...entry.tagIds.filter((t) => !familyIds.has(t))]
        await apiService.updateAnnotation(bookId, entry.chapter.filename, {
          id: sectionTagsAnnotationId(entry.headingBlockId),
          tags: nextTags,
          anchor: { type: 'block', blockId: entry.headingBlockId },
          createdAt: new Date(),
        })
        changed = true
      }
      if (changed) {
        onSectionTagsPersisted?.()
      }
      // Commit the staged flat order (Placed) — this is what updates the sidebar TOC.
      if (flatDirty) {
        await onFlatSectionsOrderChange?.(placedDraft)
      }
      onClose()
    } catch (err) {
      logger.error('Failed to save sections × elements matrix', err)
      toast.error('השמירה נכשלה')
    } finally {
      setSaving(false)
    }
  }, [
    bookId,
    saving,
    entries,
    selectedByKey,
    familyIds,
    onSectionTagsPersisted,
    flatDirty,
    placedDraft,
    onFlatSectionsOrderChange,
    onClose,
  ])

  const gridTemplateColumns = `${LABEL_W}px repeat(${elementList.length}, ${COL_W}px)`
  const headerSx = {
    position: 'sticky' as const,
    top: 0,
    zIndex: 3,
    height: HEADER_H,
    bgcolor: labelBg,
    borderInlineEnd: '1px solid',
    borderBottom: '2px solid',
    borderColor: 'divider',
    px: 0.75,
    display: 'flex',
    flexDirection: 'column' as const,
    justifyContent: 'center',
  }
  const labelSx = {
    position: 'sticky' as const,
    insetInlineStart: 0,
    zIndex: 2,
    height: ROW_H,
    bgcolor: labelBg,
    borderInlineEnd: '2px solid',
    borderBottom: '1px solid',
    borderColor: 'divider',
    px: 0.75,
    display: 'flex',
    alignItems: 'center',
    gap: 0.5,
  }

  const renderRow = (key: string, index: number, draggable: boolean) => {
    const entry = entryByKey.get(key)
    if (!entry) {
      return null
    }
    const selected = selectedByKey[key] ?? []
    const inner = (handle?: Record<string, unknown>): ReactNode => (
      <>
        <Box className="mx-rowlabel" sx={labelSx}>
          {handle && (
            <Box
              component="span"
              {...handle}
              aria-label="גרירה למיון"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                flexShrink: 0,
                cursor: 'grab',
                touchAction: 'none',
                color: 'text.disabled',
                '&:hover': { color: 'text.secondary' },
                '&:active': { cursor: 'grabbing' },
              }}
            >
              <DragIndicator sx={{ fontSize: 15 }} />
            </Box>
          )}
          <Typography
            component="span"
            sx={{ fontSize: 11, color: 'text.secondary', flexShrink: 0, minWidth: 18, textAlign: 'end' }}
          >
            {index + 1}
          </Typography>
          <IconButton
            size="small"
            onClick={() => openPreview(key)}
            aria-label="תצוגת מקטע"
            sx={{ p: 0.125, flexShrink: 0 }}
          >
            <Visibility sx={{ fontSize: 14 }} />
          </IconButton>
          <Typography
            sx={{
              flex: 1,
              minWidth: 0,
              fontSize: 12.5,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {entry.displayText}
          </Typography>
          {dependencies && (
            <SectionDependencyChip
              sectionKey={key}
              view={dependencies}
              bookDirection={bookDirection}
              onRemove={(depId) => onRemoveDependency?.(depId)}
              onEdit={(dep) => onEditDependency?.(dep)}
            />
          )}
          <Typography sx={{ fontSize: 11, color: 'text.secondary', flexShrink: 0 }}>{selected.length || ''}</Typography>
        </Box>
        {elementList.map((el) => {
          const marked = selected.includes(el.id)
          const primary = selected[0] === el.id
          const role = primary ? 'ראשי' : marked ? 'משני' : ''
          return (
            <ToggleCell
              key={`${key}::${el.id}`}
              marked={marked}
              primary={primary}
              color={tagColorMap?.[el.id]}
              title={`${entry.displayText} — ${el.name}${role ? ` (${role})` : ''}`}
              onClick={(e) => {
                if (e.metaKey || e.ctrlKey || e.altKey) {
                  setPrimary(key, el.id)
                } else {
                  toggleCell(key, el.id)
                }
              }}
            />
          )
        })}
      </>
    )

    if (draggable && flatDragEnabled) {
      return (
        <SortableMatrixRow key={`row-${key}`} id={key}>
          {(handle) => inner(handle)}
        </SortableMatrixRow>
      )
    }
    return (
      <Box key={`row-${key}`} sx={rowSx}>
        {inner()}
      </Box>
    )
  }

  const renderDivider = (label: string, count: number) => (
    // Sticky just below the column header, so the current zone's label stays pinned while scrolling.
    <Box
      sx={{
        gridColumn: '1 / -1',
        position: 'sticky',
        top: HEADER_H,
        insetInlineStart: 0,
        zIndex: 3,
        bgcolor: labelBg,
        borderTop: '1px solid',
        borderBottom: '2px solid',
        borderColor: 'divider',
        px: 1,
        py: 0.75,
      }}
    >
      <Typography sx={{ fontSize: 12, fontWeight: 800, letterSpacing: '.02em', color: 'text.primary' }}>
        {label} · {count}
      </Typography>
    </Box>
  )

  // While dragging, show both zone labels even if a zone is momentarily empty, so cross-zone drops
  // have a visible, labelled target.
  const showDividers = (placedRows.length > 0 && unplacedRows.length > 0) || (flatDragEnabled && Boolean(activeDragId))
  const placedLabel = bookDirection === 'rtl' ? 'משובצים' : 'Placed'
  const unplacedLabel = bookDirection === 'rtl' ? 'לא משובצים' : 'Unplaced'

  return (
    <>
      <Dialog open={open} onClose={handleClose} fullScreen PaperProps={{ sx: { bgcolor: 'background.default' } }}>
        <Box dir={bookDirection} sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box sx={{ flex: 1, overflow: 'auto' }}>
            {elementList.length === 0 ? (
              <Typography sx={{ p: 3, color: 'text.secondary' }}>אין יסודות מוגדרים.</Typography>
            ) : allKeys.length === 0 ? (
              <Typography sx={{ p: 3, color: 'text.secondary' }}>אין מקטעים.</Typography>
            ) : (
              <DndContext
                sensors={dragSensors}
                collisionDetection={closestCenter}
                onDragStart={onDragStart}
                onDragOver={onDragOver}
                onDragEnd={onDragEnd}
              >
                <Box sx={{ display: 'grid', gridTemplateColumns, width: 'max-content', minWidth: '100%' }}>
                  {/* Header row */}
                  <Box sx={{ ...headerSx, insetInlineStart: 0, zIndex: 4 }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary' }}>מקטע \ יסוד</Typography>
                  </Box>
                  {elementList.map((el) => (
                    <Box key={`h-${el.id}`} sx={{ ...headerSx, alignItems: 'center' }}>
                      <Typography
                        title={el.name}
                        sx={{
                          fontSize: 12.5,
                          fontWeight: 600,
                          maxWidth: '100%',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {el.name}
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{elementColCount(el.id)}</Typography>
                    </Box>
                  ))}

                  {/* Body rows: two droppable zones, each with drag-sortable rows */}
                  {showDividers && renderDivider(placedLabel, placedRows.length)}
                  <ZoneDroppable id={PLACED_ID}>
                    <SortableContext items={placedRows} strategy={verticalListSortingStrategy}>
                      {placedRows.map((key, i) => renderRow(key, i, true))}
                    </SortableContext>
                  </ZoneDroppable>
                  {showDividers && renderDivider(unplacedLabel, unplacedRows.length)}
                  <ZoneDroppable id={UNPLACED_ID}>
                    <SortableContext items={unplacedRows} strategy={verticalListSortingStrategy}>
                      {unplacedRows.map((key, i) => renderRow(key, placedRows.length + i, true))}
                    </SortableContext>
                  </ZoneDroppable>
                </Box>

                <DragOverlay dropAnimation={null}>
                  {activeDragEntry ? (
                    <Box
                      sx={{
                        px: 1,
                        py: 0.5,
                        maxWidth: LABEL_W,
                        borderRadius: 0.75,
                        border: '1px solid',
                        borderColor: 'primary.main',
                        bgcolor: 'background.paper',
                        boxShadow: 4,
                        fontSize: 12.5,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {activeDragEntry.displayText}
                    </Box>
                  ) : null}
                </DragOverlay>
              </DndContext>
            )}
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 2,
              py: 1.25,
              borderTop: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mr: 'auto', color: 'text.secondary' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 13, height: 13, borderRadius: '3px', bgcolor: 'text.secondary' }} />
                <Typography sx={{ fontSize: 11.5 }}>ראשי</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 13, height: 13, borderRadius: '3px', bgcolor: 'text.secondary', opacity: 0.4 }} />
                <Typography sx={{ fontSize: 11.5 }}>משני</Typography>
              </Box>
              <Typography sx={{ fontSize: 11.5, opacity: 0.85 }}>
                לחיצה = סימון · ⌘/Alt-לחיצה = קביעה כראשי
                {flatDragEnabled ? ' · גרירה בידית = מיון והעברה בין משובצים/לא-משובצים' : ''}
              </Typography>
            </Box>
            <Button color="inherit" onClick={handleClose}>
              ביטול
            </Button>
            <Button variant="contained" onClick={handleSave} disabled={!isDirty || saving}>
              שמירה
            </Button>
          </Box>
        </Box>
      </Dialog>
      {previewKey && (
        <Dialog
          open
          onClose={closePreview}
          maxWidth={false}
          PaperProps={{ sx: { width: 'min(72vw, 920px)', height: 'min(78vh, 760px)', direction: bookDirection } }}
        >
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', height: '100%' }}>
            <SearchPreviewPanel
              previewIdentity={previewKey}
              previewTitle={previewTitle}
              previewAst={previewAst}
              previewLoading={previewLoading}
              previewError={previewError}
              book={book ?? null}
              editable={!previewError && !previewLoading && Boolean(previewAst)}
              onChange={onPreviewChange}
              onSave={onPreviewSave}
              saving={previewSaving}
              hasUnsavedChanges={previewDirty}
              recentlySaved={previewRecentlySaved}
            />
          </Box>
        </Dialog>
      )}
    </>
  )
}
